<template>
  <slot></slot>
</template>
<script lang="ts">
import { deepEqual, lateinit, typed } from 'tg-commons'
import { BaiduMarker, bindEvents, LatLng, type MarkerEventMap, type MarkerLabel, type MarkerOptions, type MarkerOverlay, type UnionIcon } from 'tg-map-core'
import { defineComponent, toRaw, type PropType } from 'vue'
import { callHook, eventProp, optionalProp, requiredProp, safeAsComponent, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../../utils/vue-utils'
import TgMarkerClusterer from '../extra/TgMarkerClusterer.vue'
import MapMixin, { MIXIN_HOOK_CREATE, MIXIN_HOOK_DESTROY } from '../map-mixin'
import type TgInfoBox from './TgInfoBox.vue'
import type TgInfoWindow from './TgInfoWindow.vue'
import type TgLabel from './TgLabel.vue'

type TgMarkerEmits = {
  'update:position': LatLng,
}

export type TgMarkerEventEmits = EventEmits<MarkerEventMap & TgMarkerEmits>

export const tgMarkerProps = {
  position: requiredProp(LatLng),
  title: optionalProp(String),
  label: optionalProp(Object as PropType<MarkerLabel>),
  icon: optionalProp(Object as PropType<UnionIcon>),
  clickable: optionalProp(Boolean),
  draggable: optionalProp(Boolean),
  crossOnDrag: optionalProp(Boolean),
  cursor: optionalProp(String),
  zIndex: optionalProp(Number),
  visible: optionalProp(Boolean),
  normalizePositionForBaidu: optionalProp(Boolean),
  /**
     * 是否自动添加到TgMarkerClusterer中去
     * @default true
     */
  autoAddToClusterer: optionalProp(Boolean, true),
  'onUpdate:position': eventProp(),
} satisfies Props<MarkerOptions & {
  autoAddToClusterer?: boolean
}> & EventProps<TgMarkerEmits>

const TgMarker = defineComponent({
  name: 'tg-marker',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: tgMarkerProps,
  emits: undefined as any as TgMarkerEventEmits,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      marker: lateinit<MarkerOverlay>(),
      labelOverlay: typed<TgLabel>(),
      info: typed<TgInfo>(),
      emittedPosition: undefined as LatLng | undefined,
      autoAddToClustererWhenCreate: props.autoAddToClusterer,
    }
  },
  watch: {
    position(value: LatLng) {
      if (this.emittedPosition != null) {
        const position = this.emittedPosition
        this.emittedPosition = undefined
        if (position === toRaw(value)) {
          // 防止emit出的值被设置回来
          return
        }
      }
      this.marker.setPosition(value)
    },
    title(value: string) {
      this.marker.setTitle(value)
    },
    label(value?: MarkerLabel, oldValue?: MarkerLabel) {
      if (deepEqual(value, oldValue)) {
        // MarkerLabel是对象, 并且大部分时候都是直接在模板里面创建的, 很多时候创建的是内容相同的对象, 此时不需要更新
        return
      }
      if (value) {
        this.marker.setLabel(value)
      } else {
        this.recreate()
      }
    },
    icon(value?: UnionIcon) {
      if (value) {
        // {@macro hot_reload_prop_change_before_on_create}
        this.marker?.setIcon(value)
      } else {
        this.recreate()
      }
    },
    clickable(value: boolean) {
      if (this.marker instanceof BaiduMarker) {
        this.recreate() // baidu不支持setClickable(), 故只能recreate()
      } else {
        this.marker.setClickable(value)
      }
    },
    draggable(value: boolean) {
      this.marker.setDraggable(value)
    },
    crossOnDrag() {
      this.recreate()
    },
    cursor() {
      this.recreate()
    },
    zIndex(value?: number) {
      if (this.marker instanceof BaiduMarker) {
        this.recreate() // 当前版本, baidu调用setZIndex()无效
      } else {
        this.marker.setZIndex(value)
      }
    },
    visible(value: boolean) {
      this.marker.setVisible(value)
    },
    normalizePositionForBaidu() {
      this.recreate()
    },
    autoAddToClusterer() {
      this.recreate()
    },
  },
  onCreate() {
    // 结构化类型, this包含MarkerOptions的所有属性, 故可以直接传进去
    this.marker = this.$map.createMarker(this)
    if (this.labelOverlay) {
      this.marker.attachLabelOverlay(this.labelOverlay.overlay)
    }

    if (this.$clusterer() && this.autoAddToClusterer) {
      this.$clusterer()!.onAddMarker(this.marker)
    } else {
      this.$map.addOverlay(this.marker)
    }
    // 保存创建时autoAddToClusterer的值, 销毁时需要使用它
    this.autoAddToClustererWhenCreate = this.autoAddToClusterer

    // baidu, 只有marker被添加到地图之后, 才能在它上面打开infoWindow
    // infoBox似乎没有这个限制, 但目前不区分infoWindow和infoBox, 故统一放在这里处理
    if (this.info) {
      this.info.show && this.info.overlay.open(this.marker)
    }

    bindEvents(this.attrs.listeners, this.marker, this.$emit)

    if (this.attrs.listenerProps['update:position']) {
      this.marker.addEventListener('dragend', (event) => {
        this.$emit('update:position', this.emittedPosition = event.position)
      })
    }
  },
  onDestroy() {
    if (this.$clusterer() && this.autoAddToClustererWhenCreate) {
      this.$clusterer()!.onRemoveMarker(this.marker)
    } else {
      this.$map.removeOverlay(this.marker)
    }
  },
  methods: {
    recreate() {
      // 重写recreate, 销毁时关闭info和labelOverlay, 保证断开他们和老的marker的关联
      callHook(this as any, MIXIN_HOOK_DESTROY)
      this.labelOverlay && this.marker.attachLabelOverlay(undefined)
      this.info && this.info.overlay.close()
      callHook(this as any, MIXIN_HOOK_CREATE)
    },
    $clusterer(): TgMarkerClusterer | undefined {
      return safeAsComponent(this.$parent, TgMarkerClusterer)
    },
    onAddLabel(label: TgLabel) {
      this.labelOverlay = label
      // onAdd方法可能在创建this.marker前调用, 需要判断下
      if (this.marker) {
        this.marker.attachLabelOverlay(label.overlay)
      }
    },
    onRemoveLabel(_label: TgLabel) {
      this.marker.attachLabelOverlay(undefined)
      this.labelOverlay = undefined
    },
    onAddInfo(info: TgInfo) {
      this.info = info
      // @see {@link onAddLabel}
      if (this.marker) {
        info.show && info.overlay.open(this.marker)
      }
    },
    onRemoveInfo(info: TgInfo) {
      info.overlay.close()
      this.info = undefined
    },
    onInfoShowChanged(info: TgInfo, isShow: boolean) {
      isShow ? info.overlay.open(this.marker) : info.overlay.close()
    },
  },
})
type TgMarker = InstanceType<typeof TgMarker>
export default TgMarker

export type TgInfo = TgInfoWindow | TgInfoBox

export type TgMarkerPublicInstance = {
  marker: MarkerOverlay

  onAddInfo(info: TgInfo): void
  onRemoveInfo(info: TgInfo): void
  onInfoShowChanged(info: TgInfo, isShow: boolean): void

  onAddLabel(label: TgLabel): void
  onRemoveLabel(label: TgLabel): void
}
</script>
